import { useEffect, useState } from 'react';
import { X, HandCoins } from '../../lib/icons';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth';
import { caisseImposee } from '../../lib/permissions';
import { useTresorerieVerrou } from '../../lib/tresorerieEtat';
import { ContactCombobox } from '../shared/ContactCombobox';
import { fetchTiers, fetchFinances, enregistrerAvanceFournisseur, avanceDisponible, MODES_PAIEMENT } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

export const AvanceFournisseurDialog = ({ onClose, onDone }) => {
  const { user } = useAuth();
  const { verrou: tresorerieKo, enCours: envoiEnCours, titre: titreVerrou } = useTresorerieVerrou();
  const caisseFixe = caisseImposee(user?.role);
  const [tiers, setTiers] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [f, setF] = useState({ tiersId: '', montant: '', mode: 'Espèces', compteId: caisseFixe ?? 'chq', date: new Date().toISOString().slice(0, 10), notes: '' });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    fetchTiers().then((rows) => setTiers(rows.filter((c) => c.estFournisseur !== false)));
    fetchFinances().then((fin) => {
      setComptes(caisseFixe ? fin.soldes.filter((c) => c.id === caisseFixe) : fin.soldes);
      if (!caisseFixe && !fin.soldes.some((c) => c.id === 'chq')) setF((p) => ({ ...p, compteId: fin.soldes[0]?.id ?? '' }));
    });
  }, [caisseFixe]);

  const dispo = f.tiersId ? avanceDisponible(f.tiersId) : 0;

  const submit = async (e) => {
    e.preventDefault();
    const { error, imputes, disponible } = await enregistrerAvanceFournisseur(f);
    if (error) return toast.error(error);
    toast.success(
      `Avance de ${fmt(Number(f.montant), 3)} TND enregistrée${imputes ? ` — imputée sur ${imputes} bon(s)` : ''} · reliquat disponible ${fmt(disponible, 3)} TND`
    );
    onDone?.();
    onClose();
  };

  return (
    <div data-testid="avance-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={submit} data-testid="avance-form" className="w-full max-w-lg border border-slate-200 border-t-4 border-t-amber-500 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <HandCoins size={18} className="text-amber-600" /> Avance fournisseur
            </h2>
            <p className="text-xs text-slate-500">Versée avant livraison, imputée automatiquement sur les prochains bons de réception.</p>
          </div>
          <button type="button" data-testid="avance-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className={label}>Fournisseur</label>
            <ContactCombobox contacts={tiers} value={f.tiersId} onChange={(id) => setF((p) => ({ ...p, tiersId: id }))} testid="avance-tiers" />
            {f.tiersId && (
              <p data-testid="avance-dispo-actuelle" className="mt-1.5 border-l-2 border-amber-500 bg-amber-50 px-2 py-1 font-data text-[11px] text-amber-800">
                Avance déjà disponible : {fmt(dispo, 3)} TND
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Montant (TND)</label>
              <input data-testid="avance-montant" type="number" step="0.001" min="0" value={f.montant} onChange={set('montant')} className={field} />
            </div>
            <div>
              <label className={label}>Date</label>
              <input data-testid="avance-date" type="date" value={f.date} onChange={set('date')} className={field} />
            </div>
            <div>
              <label className={label}>Mode de paiement</label>
              <select data-testid="avance-mode" value={f.mode} onChange={set('mode')} className={field}>
                {MODES_PAIEMENT.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Caisse / compte débité</label>
              <select data-testid="avance-compte" value={f.compteId} onChange={set('compteId')} disabled={Boolean(caisseFixe)} className={`${field} disabled:bg-slate-100`}>
                {comptes.map((c) => <option key={c.id} value={c.id}>{c.nom} — {fmt(c.solde, 3)} TND</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={label}>Note</label>
            <input data-testid="avance-notes" value={f.notes} onChange={set('notes')} placeholder="Ex. : avance cueillette, 3 voyages prévus" className={field} />
          </div>
        </div>
        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="avance-enregistrer"
            disabled={tresorerieKo}
            title={titreVerrou}
            className="h-11 flex-1 bg-amber-600 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {envoiEnCours ? 'Enregistrement en cours…' : tresorerieKo ? 'Trésorerie bloquée' : 'Enregistrer l’avance'}
          </button>
          <button type="button" data-testid="avance-annuler" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
